import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { useHistory } from "react-router-dom";

interface VerifiedClaim {
  claim: string;
  document_location: string;
  claim_type: string;
  cited_source?: string | null;
  verdict: string;
  evidence: string;
  research_direction?: string | null;
}

interface DocumentVerificationRow {
  id: number;
  status: string;
  source_data_record_id?: number | null;
  artifact_id?: number | null;
  document_name?: string | null;
  dataset_id?: number | null;
  sdr_id?: number | null;
  evidence_dataset_ids: number[];
  evidence_note?: string | null;
  claims?: VerifiedClaim[] | null;
  summary?: string | null;
  tier?: string | null;
  counts?: Record<string, number> | null;
  source: string;
  source_ref_id?: number | null;
  dialogue_id?: number | null;
  create_date?: string | null;
}

interface DatasetOption {
  id: number;
  name: string;
}

interface DocumentOption {
  id: number;
  label: string;
}

const TIER_COLOR: Record<string, "default" | "success" | "warning" | "error"> =
  {
    corroborated: "success",
    verified_with_gaps: "warning",
    unsupported_claims: "warning",
    contradicted: "error",
  };

const VERDICT_COLOR: Record<
  string,
  "default" | "success" | "warning" | "error"
> = {
  supported: "success",
  partially_supported: "warning",
  unsupported: "warning",
  contradicted: "error",
  not_independently_verifiable: "default",
};

// Detail view ordering: the reviewer's attention should land on the gaps.
const VERDICT_ORDER = [
  "contradicted",
  "unsupported",
  "partially_supported",
  "not_independently_verifiable",
  "supported",
];

const TIER_FILTERS = [
  "corroborated",
  "verified_with_gaps",
  "unsupported_claims",
  "contradicted",
] as const;

const claimsSummary = (counts?: Record<string, number> | null): string => {
  if (!counts) return "—";
  const parts: string[] = [];
  const supported = counts.supported ?? 0;
  if (supported) parts.push(`${supported} ✓`);
  if (counts.partially_supported)
    parts.push(`${counts.partially_supported} partial`);
  if (counts.unsupported) parts.push(`${counts.unsupported} unsupported`);
  if (counts.contradicted) parts.push(`${counts.contradicted} contradicted`);
  if (counts.not_independently_verifiable)
    parts.push(`${counts.not_independently_verifiable} unverifiable`);
  return parts.join(" · ") || "—";
};

const StatusChip = ({ row }: { row: DocumentVerificationRow }) => {
  if (row.status === "complete" && row.tier) {
    return (
      <Chip
        label={row.tier.replace(/_/g, " ")}
        size="small"
        color={TIER_COLOR[row.tier] ?? "default"}
        data-testid={`verification-tier-${row.id}`}
      />
    );
  }
  if (row.status === "pending") {
    return <Chip label="verifying…" size="small" variant="outlined" />;
  }
  return <Chip label="failed" size="small" color="error" variant="outlined" />;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="overline"
    sx={{ display: "block", color: "text.secondary", lineHeight: 2, mt: 2 }}
  >
    {children}
  </Typography>
);

/**
 * VerificationsPage — platform-wide browser over document claims
 * verifications. Lists every verification (any source: manual requests and
 * product-driven ones like 50.59 report reviews) with its trust tier; row
 * click opens the full claims review, grouped worst-verdict-first, with the
 * per-claim evidence and the targeted research direction a human reviewer
 * should follow. "Verify a document" dispatches the Document Verification
 * Reviewer over any existing platform document.
 */
const VerificationsPage = () => {
  const history = useHistory();
  const [rows, setRows] = useState<DocumentVerificationRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tierFilter, setTierFilter] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [selected, setSelected] = useState<DocumentVerificationRow | null>(
    null,
  );
  const [showSupported, setShowSupported] = useState(false);

  // "Verify a document" dialog state
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [datasets, setDatasets] = useState<DatasetOption[]>([]);
  const [documents, setDocuments] = useState<DocumentOption[]>([]);
  const [pickedDataset, setPickedDataset] = useState<number | "">("");
  const [pickedDocument, setPickedDocument] = useState<number | "">("");
  const [evidenceDatasets, setEvidenceDatasets] = useState<number[]>([]);
  const [guidanceNote, setGuidanceNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, string | number> = { limit: 200 };
      if (sourceFilter) params.source = sourceFilter;
      const response = await axios.get(
        `${nuclearn_api_url}/v1/verification/documents`,
        { params },
      );
      setRows(response.data ?? []);
    } catch {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [sourceFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!verifyOpen || datasets.length > 0) return;
    axios
      .get(`${nuclearn_api_url}/datasets/`, {
        params: { dataset_type_id: 2, only_logically_deleted: false },
      })
      .then((response) =>
        setDatasets(
          (response.data ?? []).map((d: any) => ({ id: d.id, name: d.name })),
        ),
      )
      .catch(() => setDatasets([]));
  }, [verifyOpen, datasets.length]);

  useEffect(() => {
    if (pickedDataset === "") {
      setDocuments([]);
      return;
    }
    axios
      .get(`${nuclearn_api_url}/datasets/${pickedDataset}/records`, {
        params: { record_idx: 0, num_recs_per_page: 200 },
      })
      .then((response) =>
        setDocuments(
          (response.data ?? []).map((r: any) => ({
            id: r.id,
            label:
              r.data?.file_name ?? r.source_uid ?? `record ${r.id as number}`,
          })),
        ),
      )
      .catch(() => setDocuments([]));
  }, [pickedDataset]);

  const filteredRows = useMemo(
    () =>
      tierFilter
        ? rows.filter((r) => r.tier === tierFilter)
        : rows,
    [rows, tierFilter],
  );

  const sortedClaims = useMemo(() => {
    if (!selected?.claims) return [];
    return [...selected.claims].sort(
      (a, b) =>
        VERDICT_ORDER.indexOf(a.verdict) - VERDICT_ORDER.indexOf(b.verdict),
    );
  }, [selected]);

  const unresolvedClaims = sortedClaims.filter(
    (c) => c.verdict !== "supported",
  );
  const supportedClaims = sortedClaims.filter(
    (c) => c.verdict === "supported",
  );

  const submitVerification = async () => {
    if (pickedDocument === "") return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${nuclearn_api_url}/v1/verification/documents`, {
        source_data_record_id: pickedDocument,
        evidence_dataset_ids: evidenceDatasets,
        evidence_note: guidanceNote || null,
        source: "manual",
      });
      setVerifyOpen(false);
      setPickedDataset("");
      setPickedDocument("");
      setEvidenceDatasets([]);
      setGuidanceNote("");
      loadRows();
    } catch (error: any) {
      setSubmitError(
        error?.response?.data?.detail ?? "Failed to request verification.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDocument = (row: DocumentVerificationRow) => {
    if (row.dataset_id && row.sdr_id) {
      history.push(`/datasets/${row.dataset_id}/document/${row.sdr_id}`);
    }
  };

  const ClaimRow = ({ claim }: { claim: VerifiedClaim }) => (
    <TableRow>
      <TableCell sx={{ width: 110, verticalAlign: "top" }}>
        <Chip
          label={claim.verdict.replace(/_/g, " ")}
          size="small"
          color={VERDICT_COLOR[claim.verdict] ?? "default"}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
          {claim.claim}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {claim.document_location}
          {claim.cited_source ? ` · cites ${claim.cited_source}` : ""} —{" "}
          {claim.evidence}
        </Typography>
        {claim.research_direction && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: "warning.main" }}
          >
            Check: {claim.research_direction}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3 }} data-testid="verifications-page">
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Document Verifications
        </Typography>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Trust tier</InputLabel>
          <Select
            label="Trust tier"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
          >
            <MenuItem value="">All tiers</MenuItem>
            {TIER_FILTERS.map((t) => (
              <MenuItem key={t} value={t}>
                {t.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Source</InputLabel>
          <Select
            label="Source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <MenuItem value="">All sources</MenuItem>
            <MenuItem value="manual">manual</MenuItem>
            <MenuItem value="fifty_fifty_nine">50.59</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setVerifyOpen(true)}
          data-testid="verify-a-document"
        >
          Verify a document
        </Button>
        <Tooltip title="Refresh">
          <IconButton onClick={loadRows} disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Table size="small" data-testid="verifications-table">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Document</TableCell>
            <TableCell>Result</TableCell>
            <TableCell>Claims</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Requested</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRows.map((row) => (
            <TableRow
              key={row.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setSelected(row);
                setShowSupported(false);
              }}
              data-testid={`verification-row-${row.id}`}
            >
              <TableCell>{row.id}</TableCell>
              <TableCell sx={{ maxWidth: 360 }}>
                <Typography variant="body2" noWrap>
                  {row.document_name ??
                    (row.artifact_id
                      ? `artifact ${row.artifact_id}`
                      : `document ${row.source_data_record_id}`)}
                </Typography>
              </TableCell>
              <TableCell>
                <StatusChip row={row} />
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {claimsSummary(row.counts)}
                </Typography>
              </TableCell>
              <TableCell>
                {row.source === "fifty_fifty_nine" ? (
                  <Chip
                    label={`50.59 record #${row.source_ref_id}`}
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      history.push("/assistant/records");
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {row.source}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {row.create_date
                    ? new Date(row.create_date).toLocaleDateString()
                    : "—"}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {row.dataset_id && row.sdr_id && (
                  <Tooltip title="Open document">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDocument(row);
                      }}
                    >
                      <DescriptionOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!isLoading && filteredRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 3, textAlign: "center" }}
                >
                  No verifications yet. Use &quot;Verify a document&quot; to
                  run an independent claims review of any document on the
                  platform.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Verification detail */}
      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" component="div" sx={{ flexShrink: 1 }}>
                  Verification #{selected.id} —{" "}
                  {selected.document_name ?? "document"}
                </Typography>
                <StatusChip row={selected} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {claimsSummary(selected.counts)}
              </Typography>
              <IconButton
                onClick={() => setSelected(null)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                {selected.dataset_id && selected.sdr_id && (
                  <Button
                    size="small"
                    startIcon={<DescriptionOutlinedIcon />}
                    onClick={() => openDocument(selected)}
                  >
                    Open document
                  </Button>
                )}
                {selected.dialogue_id && (
                  <Button
                    size="small"
                    startIcon={<ForumOutlinedIcon />}
                    onClick={() =>
                      history.push(`/assistant/chat/${selected.dialogue_id}`)
                    }
                  >
                    Open verification conversation
                  </Button>
                )}
                {selected.source === "fifty_fifty_nine" && (
                  <Button
                    size="small"
                    onClick={() => history.push("/assistant/records")}
                  >
                    Open 50.59 records
                  </Button>
                )}
              </Box>

              {selected.summary && (
                <>
                  <SectionTitle>Reviewer summary</SectionTitle>
                  <Typography variant="body2">{selected.summary}</Typography>
                </>
              )}

              {selected.evidence_note && (
                <>
                  <SectionTitle>Verification guidance</SectionTitle>
                  <Typography variant="caption" color="text.secondary">
                    {selected.evidence_note}
                  </Typography>
                </>
              )}

              {selected.status === "complete" && (
                <>
                  <SectionTitle>
                    Needs attention ({unresolvedClaims.length})
                  </SectionTitle>
                  {unresolvedClaims.length > 0 ? (
                    <Table size="small">
                      <TableBody>
                        {unresolvedClaims.map((claim, idx) => (
                          <ClaimRow key={idx} claim={claim} />
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Every claim was independently corroborated.
                    </Typography>
                  )}

                  <SectionTitle>
                    Corroborated ({supportedClaims.length})
                    <Button
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => setShowSupported((s) => !s)}
                    >
                      {showSupported ? "hide" : "show"}
                    </Button>
                  </SectionTitle>
                  {showSupported && (
                    <Table size="small">
                      <TableBody>
                        {supportedClaims.map((claim, idx) => (
                          <ClaimRow key={idx} claim={claim} />
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}

              {selected.status === "pending" && (
                <Typography variant="body2" color="text.secondary">
                  The verification pipeline is running: the document is
                  windowed, claims are extracted per window, and each claim
                  is checked against the evidence datasets. Refresh to see
                  the result.
                </Typography>
              )}

              {selected.status === "failed" && (
                <Typography variant="body2" color="error">
                  {selected.summary ??
                    "The verification failed. Re-run it from the document."}
                </Typography>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Verify-a-document dialog */}
      <Dialog
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify a document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            An independent reviewer agent enumerates every material claim in
            ONE document and re-verifies each against the evidence datasets,
            returning per-claim verdicts and targeted checks for a human
            reviewer.
          </Typography>
          <SectionTitle>Document to verify</SectionTitle>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Every document lives in a dataset — pick where it lives, then the
            document itself. Agent-generated reports (e.g. 50.59 records) are
            in &quot;LLM Generated Documents (Tool Updatable)&quot;.
          </Typography>
          <Autocomplete
            size="small"
            sx={{ mb: 2 }}
            options={datasets}
            getOptionLabel={(d) => d.name}
            value={datasets.find((d) => d.id === pickedDataset) ?? null}
            onChange={(_e, value) => {
              setPickedDataset(value?.id ?? "");
              setPickedDocument("");
            }}
            renderInput={(params) => (
              <TextField {...params} label="Dataset the document lives in" />
            )}
          />
          <Autocomplete
            size="small"
            sx={{ mb: 2 }}
            options={documents}
            disabled={pickedDataset === ""}
            getOptionLabel={(d) => d.label}
            value={documents.find((d) => d.id === pickedDocument) ?? null}
            onChange={(_e, value) => setPickedDocument(value?.id ?? "")}
            renderInput={(params) => (
              <TextField {...params} label="Document to verify" />
            )}
          />
          <SectionTitle>Evidence corpus</SectionTitle>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            The datasets the reviewer searches to confirm or refute the
            document&apos;s claims.
          </Typography>
          <Autocomplete
            size="small"
            sx={{ mb: 2 }}
            multiple
            options={datasets}
            getOptionLabel={(d) => d.name}
            value={datasets.filter((d) => evidenceDatasets.includes(d.id))}
            onChange={(_e, values) =>
              setEvidenceDatasets(values.map((v) => v.id))
            }
            renderInput={(params) => (
              <TextField {...params} label="Evidence datasets" />
            )}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Verification guidance (optional)"
            placeholder="Domain context for the reviewer, e.g. what the document is and what to scrutinize."
            value={guidanceNote}
            onChange={(e) => setGuidanceNote(e.target.value)}
          />
          {submitError && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {submitError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={pickedDocument === "" || isSubmitting}
            onClick={submitVerification}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            data-testid="submit-verification"
          >
            Start verification
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificationsPage;
